import React from "react";
import { Row, Col, Button, ListGroup, Alert, Form } from "react-bootstrap";
import { post } from "aws-amplify/api";
import Moment from "moment";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function TitleDetailsList({
    data,
    isLoading,
    isAuthenticated = true,
    controlType,
    saveRecord,
    deleteRecord,
    updateTableState,
    filter,
    user,
    validateRelatedRecord,
    dataFetch,
    stateCode,
}) {
    const dataSet = "titleDetails";
    // Local copy of the data, mirroring UAT's this.state.data pattern.
    // Allows immediate geometry update from dataFetch return value without
    // waiting for the full parent → prop → child re-render chain.
    const [localData, setLocalData] = React.useState(data || []);
    React.useEffect(() => {
        setLocalData(data || []);
    }, [data]);
    const tableData = localData;
    const normalizedStateCode = String(stateCode || "").toUpperCase();
    const columnHelper = createColumnHelper();
    const [showAlert, setShowAlert] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [copyInProgress, setCopyInProgress] = React.useState(false);

    const tableMeta = {
        updateData: (rowIndex, columnId, value) => {
            updateTableState(dataSet, rowIndex, columnId, value);
        },
        handleSave: async ({ row, column, value }) => {
            const record = {
                ...(tableData[row.index] || {}),
                ...row.original,
                ...(column?.id ? { [column.id]: value } : {}),
            };
            const invalidPrefix = record?.parcel_id
                ? `Parcel #${record.parcel_id} invalid and was NOT saved:`
                : "New parcel invalid and was NOT saved:";

            // Keep ACT Section feedback explicit for the edited column.
            if (
                normalizedStateCode === "ACT" &&
                column?.id === "section" &&
                !/^\d{1,3}$/.test(String(value ?? "").trim())
            ) {
                setErrorMessage(`${invalidPrefix}\nSection not 1-3 digits`);
                setShowAlert(true);
                return { validationFailed: true };
            }

            // UAT parity: for parsed-field edits, clear parcel description so backend
            // recalculates derived values (including geometry) on save.
            if (column?.id && column.id !== "jurisdiction_id") {
                record.jurisdiction_id = null;
            }

            const isNew = record.new_record === true || !record.parcel_id;

            // Always run validation and show errors immediately
            const titleValidation = validateTitleDetails(record);
            if (titleValidation[0] !== true) {
                setErrorMessage(`${invalidPrefix}\n${titleValidation[1]}`);
                setShowAlert(true);
                return { validationFailed: true };
            }

            const validation = validateRelatedRecord?.(record, dataSet) || [true, null];
            if (validation[0] !== true) {
                setErrorMessage(validation[1] || "Validation failed");
                setShowAlert(true);
                return { validationFailed: true };
            }

            // For new records, only call the API once a sufficient field is filled.
            if (isNew) {
                const effectivePlanNumber = column.id === 'plan_number' ? value : record.plan_number;
                const effectiveParcelDescription =
                    column.id === "jurisdiction_id" ? value : record.jurisdiction_id;

                const hasPlanNumber = Boolean(
                    effectivePlanNumber && String(effectivePlanNumber).trim()
                );
                const hasParcelDescription = Boolean(
                    effectiveParcelDescription && String(effectiveParcelDescription).trim()
                );

                // ACT doesn't use plan_number or jurisdiction_id — its parsed fields
                // are block, section, district, and division. Allow the first save as
                // soon as any of those is filled (UAT parity: validate-then-save).
                const hasACTField = normalizedStateCode === "ACT" && Boolean(
                    (record.block && String(record.block).trim()) ||
                    (record.section && String(record.section).trim()) ||
                    (record.district && String(record.district).trim()) ||
                    (record.division && String(record.division).trim())
                );

                // UAT parity: allow first save from either parsed fields (plan number),
                // parcel description text, or ACT-specific parsed fields.
                if (!hasPlanNumber && !hasParcelDescription && !hasACTField) {
                    // Validation passed but not ready to persist yet — just update local state
                    setShowAlert(false);
                    setErrorMessage("");
                    return { skipSave: true };
                }
            }

            setShowAlert(false);
            setErrorMessage("");

            try {
                await saveRecord(dataSet, record);
                // Refresh all parcels from the server so auto-populated fields
                // (jurisdiction_id, state_code, parcel_id, geometry) are reflected
                // immediately — aligned with UAT's refetchData() pattern.
                if (dataFetch) {
                    const freshData = await dataFetch();
                    // UAT parity: update local state directly from the dataFetch return
                    // value so geometry shows without waiting for the parent re-render cycle.
                    if (Array.isArray(freshData)) {
                        setLocalData(freshData);
                    }
                }
            } catch (error) {
                console.error("Title details save failed:", error);
                return { saveFailed: true };
            }

            // Skip EditableCell's partial local updateData — the full refresh
            // from dataFetch() already replaced the row in parent state.
            return { skipLocalUpdate: true };
        },
        handleDelete: async ({ row }) => {
            await deleteRecord(dataSet, row.original);

            const deletedId = row.original.parcel_id;
            const deletedClientId = row.original._client_id;
            const nextRows = tableData.filter((item) => {
                const matchesSavedId =
                    deletedId !== null &&
                    deletedId !== undefined &&
                    String(item.parcel_id) === String(deletedId);
                const matchesClientId =
                    deletedClientId !== null &&
                    deletedClientId !== undefined &&
                    item._client_id === deletedClientId;

                return !(matchesSavedId || matchesClientId);
            });

            updateTableState(nextRows, dataSet);
            setLocalData(nextRows);
        },
    };

    const columns = [
        columnHelper.accessor("parcel_id", {
            id: "parcel_id",
            header: "ID",
            size: 65,
        }),
        columnHelper.accessor("jurisdiction_id", {
            id: "jurisdiction_id",
            header: "Parcel Description",
            cell: (cellContext) => {
                const value = cellContext.getValue();
                return value ?? "";
            },
            size: 200,
            meta: { 
                wrap: true,
                cellClassName: "lavender-cell",
            },
        }),
        ...(normalizedStateCode === "SA"
            ? [
                  columnHelper.accessor("lot_type", {
                      id: "lot_type",
                      header: "Lot Type",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["ACT", "VIC"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("block", {
                      id: "block",
                      header: "Block",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["ACT", "NSW", "VIC"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("section", {
                      id: "section",
                      header: "Section",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "VIC"
            ? [
                  columnHelper.accessor("portion", {
                      id: "portion",
                      header: "Portion",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "VIC"
            ? [
                  columnHelper.accessor("crown_allotment_number", {
                      id: "crown_allotment_number",
                      header: "Crown Allotment Number",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("lot_number", {
                      id: "lot_number",
                      header: "Lot Number",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["NSW", "QLD", "SA", "VIC", "WA"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("plan_prefix", {
                      id: "plan_prefix",
                      header: "Plan Prefix",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["NSW", "QLD", "SA", "TAS", "VIC", "WA"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("plan_number", {
                      id: "plan_number",
                      header: "Plan Number",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "NT"
            ? [
                  columnHelper.accessor("survey_plan", {
                      id: "survey_plan",
                      header: "Survey Plan",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(["ACT", "NT"].includes(normalizedStateCode)
            ? [
                  columnHelper.accessor("unit", {
                      id: "unit",
                      header: "Unit",
                      cell: EditableCell,
                      size: 70,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "ACT"
            ? [
                  columnHelper.accessor("district", {
                      id: "district",
                      header: "District",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "ACT"
            ? [
                  columnHelper.accessor("division", {
                      id: "division",
                      header: "Divison",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "NT"
            ? [
                  columnHelper.accessor("location_code", {
                      id: "location_code",
                      header: "Location Code",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        ...(normalizedStateCode === "VIC"
            ? [
                  columnHelper.accessor("parish_code", {
                      id: "parish_code",
                      header: "Parish Code",
                      cell: EditableCell,
                      size: 100,
                  }),
              ]
            : []),
        columnHelper.accessor("state_code", {
            id: "state_code",
            header: "State Code",
            size: 80,
        }),
        columnHelper.accessor("geometry", {
            id: "geometry",
            header: "Geometry",
            cell: ({ getValue }) => {
                const value = getValue();
                const trimmedValue = value ? String(value).trim() : "";

                return (
                    <Form.Control
                        type="text"
                        as="textarea"
                        rows={2}
                        className="no-border form-control"
                        style={{ minHeight: "calc(2 * 1.5em + 0.75rem + 2px)" }}
                        value={trimmedValue}
                        readOnly
                        disabled
                    />
                );
            },
            size: 250,
        }),
        columnHelper.accessor("created_date", {
            id: "created_date",
            header: "Created Date",
            cell: (info) => {
                const value = info.getValue();
                return value ? Moment(value).local().format("DD-MM-YYYY") : null;
            },
            size: 120,
        }),
        columnHelper.accessor("created_by", {
            id: "created_by",
            header: "Created By",
            size: 120,
        }),
        columnHelper.accessor("last_edited_date", {
            id: "last_edited_date",
            header: "Last Edited Date",
            cell: (info) => {
                const value = info.getValue();
                return value ? Moment(value).local().format("DD-MM-YYYY") : null;
            },
            size: 120,
        }),
        columnHelper.accessor("last_edited_by", {
            id: "last_edited_by",
            header: "Last Edited By",
            size: 120,
        }),
        columnHelper.display({
            id: "delete_parcel",
            header: "",
            cell: ({ row }) => (
                <Button
                    id={`title-details-delete-${row.original.parcel_id ?? row.index}`}
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={async (event) => {
                        event.preventDefault();
                        const confirmed = window.confirm("Are you sure you want to delete this record?");
                        if (!confirmed) {
                            return;
                        }
                        await tableMeta.handleDelete({ row });
                    }}
                >
                    Delete
                </Button>
            ),
            size: 85,
            enableSorting: false,
        }),
        columnHelper.display({
            id: "copy_parcel",
            header: "",
            cell: ({ row }) => (
                <Button
                    id={`title-details-copy-${row.original.parcel_id ?? row.index}`}
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={async (event) => {
                        event.preventDefault();

                        const numCopiesStr = window.prompt(
                            "How many copies do you want to create? (Lot Number will be incremented with each copy.)"
                        );
                        if (numCopiesStr === null) return; // cancelled
                        const numCopies = parseInt(numCopiesStr, 10);
                        if (!numCopies || numCopies < 1) return;

                        setCopyInProgress(true);
                        try {
                            const copies = [];
                            for (let i = 0; i < numCopies; i++) {
                                let incrementedLotNumber = row.original.lot_number;
                                if (parseInt(row.original.lot_number, 10)) {
                                    const next = parseInt(row.original.lot_number, 10) + i + 1;
                                    if (next <= 9999) {
                                        incrementedLotNumber = String(next);
                                    }
                                }
                                copies.push({
                                    ...row.original,
                                    property_id: row.original.property_id,
                                    parcel_id: null,
                                    lot_number: incrementedLotNumber,
                                    jurisdiction_id: null,
                                    geometry: null,
                                    created_by: user,
                                    last_edited_by: user,
                                    created_date: null,
                                    last_edited_date: null,
                                });
                            }

                            // Call parcels_bulk endpoint to save copies to database
                            const propertyId = filter?.propertyid;
                            const parcelId = row.original.parcel_id;
                            if (!propertyId) {
                                alert("Property ID not found");
                                return;
                            }
                            if (!parcelId) {
                                alert("Parcel ID not found");
                                return;
                            }

                            const { body } = await post({
                                apiName: "colin",
                                path: `/properties/${propertyId}/parcels_bulk/${parcelId}`,
                                options: { body: copies },
                            }).response;

                            await body.json();

                            // Refresh all parcels from server
                            if (dataFetch) {
                                const freshData = await dataFetch();
                                if (Array.isArray(freshData)) {
                                    setLocalData(freshData);
                                }
                            }
                        } catch (error) {
                            console.error("Copy parcel failed:", error);
                            alert("Failed to copy parcels: " + error.message);
                        } finally {
                            setCopyInProgress(false);
                        }
                    }}
                    disabled={copyInProgress}
                >
                    Copy
                </Button>
            ),
            size: 80,
            enableSorting: false,
        }),
    ];

    // --- Validation Functions (State-specific) ---
    const validateTitleDetailsACT = (record) => {
        if (!/^[A-Z]{4}$/.test(record.district || "")) {
            return [false, 'District not 4 uppercase letters'];
        }
        if (!/^[A-Z]{4}$/.test(record.division || "")) {
            return [false, 'Division not 4 uppercase letters'];
        }
        if (!/^\d{1,3}$/.test(record.section || "")) {
            return [false, 'Section not 1-3 digits'];
        }
        if (!/^\d{1,4}$/.test(record.block || "")) {
            return [false, 'Block not 1-4 digits'];
        }
        if (record.unit && !/^\d{1,3}$/.test(record.unit)) {
            return [false, 'Unit not 1-3 digits'];
        }
        return [true, null];
    };

    const validateTitleDetailsNSW = (record) => {
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (record.section && !/^[A-Z\d]+$/.test(record.section)) {
            return [false, 'Section not alphanumeric'];
        }
        if (record.plan_prefix && !/^[A-Z]{1,2}$/.test(record.plan_prefix)) {
            return [false, 'Plan Prefix not 1-2 uppercase letters'];
        }
        if (!/^\d+$/.test(record.plan_number || "")) {
            return [false, 'Plan number not numeric'];
        }
        return [true, null];
    };

    const validateTitleDetailsNT = (record) => {
        if (!/^\d{3}$/.test(record.location_code)) {
            return [false, 'Location Code not 3 digits'];
        }
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (record.survey_plan && !/^[A-Z\d]+ *[A-Z\/\d]+$/.test(record.survey_plan)) {
            return [false, 'Survey Plan not alphanumeric with (optional) forward-slashes'];
        }
        if (record.unit && !/^\d+$/.test(record.unit)) {
            return [false, 'Unit not numeric'];
        }
        return [true, null];
    };

    const validateTitleDetailsQLD = (record) => {
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (!/^[A-Z]{1,3}$/.test(record.plan_prefix || "")) {
            return [false, 'Plan Prefix not 1-3 uppercase letters'];
        }
        if (!/^\d+$/.test(record.plan_number || "")) {
            return [false, 'Plan Number not numeric'];
        }
        return [true, null];
    };

    const validateTitleDetailsSA = (record) => {
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (!record.plan_prefix) {
            return [false, 'Plan Prefix must be set'];
        }
        if (!record.plan_number) {
            return [false, 'Plan Number must be set'];
        }
        return [true, null];
    };

    const validateTitleDetailsTAS = (record) => {
        if (record.lot_number && !/^\d+$/.test(record.lot_number)) {
            return [false, 'Lot Number not numeric'];
        }
        if (!/^\d+$/.test(record.plan_number || "")) {
            return [false, 'Plan Number not numeric'];
        }
        return [true, null];
    };

    const validateTitleDetailsVIC = (record) => {
        const atLeastOneOtherAttributeSet = (
            record.crown_allotment_number || record.section || record.portion || record.block
        );
        if ((record.parish_code && !atLeastOneOtherAttributeSet) || 
            (!record.parish_code && atLeastOneOtherAttributeSet)) {
            return [false, 'Parish Code and (Crown Allotment Number or Section or Portion or Block) must be set together'];
        }
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (record.plan_prefix && !/^[A-Z]{2}$/.test(record.plan_prefix)) {
            return [false, 'Plan Prefix not 2 uppercase letters'];
        }
        if (record.plan_number && !/^[A-Z\d]+$/.test(record.plan_number)) {
            return [false, 'Plan Number not alphanumeric'];
        }
        if (record.crown_allotment_number && !/^[A-Z\d]+$/.test(record.crown_allotment_number)) {
            return [false, 'Crown Allotment Number not alphanumeric'];
        }
        if (record.section && !/^[A-Z\d]+$/.test(record.section)) {
            return [false, 'Section not alphanumeric'];
        }
        if (record.block && !/^[A-Z\d]+$/.test(record.block)) {
            return [false, 'Block not alphanumeric'];
        }
        if (record.portion && !/^[A-Z\d]+$/.test(record.portion)) {
            return [false, 'Portion not alphanumeric'];
        }
        if (record.parish_code && !/^\d{4}$/.test(record.parish_code)) {
            return [false, 'Parish Code not 3 digits'];
        }
        return [true, null];
    };

    const validateTitleDetailsWA = (record) => {
        if (record.lot_type && !/^[A-Z]+$/.test(record.lot_type)) {
            return [false, 'Lot Type not uppercase letters'];
        }
        if (record.lot_number && !/^[A-Z\d]+$/.test(record.lot_number)) {
            return [false, 'Lot Number not alphanumeric'];
        }
        if (!/^[A-Z]{1}$/.test(record.plan_prefix || "")) {
            return [false, 'Plan Prefix not 1 uppercase letter'];
        }
        if (!/^\d+$/.test(record.plan_number || "")) {
            return [false, 'Plan Number not numeric'];
        }
        return [true, null];
    };

    const validateTitleDetails = (record) => {
        const validatorsForState = {
            'ACT': validateTitleDetailsACT,
            'NSW': validateTitleDetailsNSW,
            'NT': validateTitleDetailsNT,
            'QLD': validateTitleDetailsQLD,
            'SA': validateTitleDetailsSA,
            'TAS': validateTitleDetailsTAS,
            'VIC': validateTitleDetailsVIC,
            'WA': validateTitleDetailsWA,
        };

        const validator = validatorsForState[normalizedStateCode];
        if (validator) {
            return validator(record);
        }

        // Default fallback: just check that plan_prefix is set if other fields are
        const hasParcelDetails = record.lot_type || record.lot_number || record.plan_number;
        if (hasParcelDetails && !record.plan_prefix) {
            return [false, "Plan Prefix must be set"];
        }
        return [true, null];
    };

    // --- API Functions ---
    async function addRecord() {
        const nextRows = [
            {
                _client_id: `parcel-${Date.now()}-${tableData.length}`,
                property_id: filter?.propertyid,
                jurisdiction_id: "",
                block: null,
                section: null,
                portion: null,
                crown_allotment_number: null,
                lot_type: null,
                lot_number: null,
                plan_prefix: null,
                plan_number: null,
                survey_plan: null,
                unit: null,
                district: null,
                division: null,
                location_code: null,
                parish_code: null,
                state_code: null,
                geometry: null,
                created_by: user,
                last_edited_by: user,
                archived_date: null,
                new_record: true,
            },
            ...tableData,
        ];

        updateTableState(nextRows, dataSet);
        setLocalData(nextRows);
    }

    async function deleteAll(event) {
        event.preventDefault();
        if ((tableData || []).length === 0) {
            return;
        }

        const confirmed = window.confirm("Are you sure you want to delete all Parcels?");
        if (!confirmed) {
            return;
        }

        // Aligned with UAT: iterate in reverse, archive saved records via API,
        // remove unsaved records (no parcel_id) directly from local state.
        const rows = [...tableData];
        for (let i = rows.length - 1; i >= 0; i--) {
            const row = rows[i];
            if (!row.parcel_id) {
                // Unsaved row — remove locally only
                rows.splice(i, 1);
                updateTableState([...rows], dataSet);
                setLocalData([...rows]);
            } else {
                // Saved row — archive on backend then remove locally
                try {
                    await deleteRecord(dataSet, { ...row, archived_date: new Date() });
                    rows.splice(i, 1);
                    updateTableState([...rows], dataSet);
                    setLocalData([...rows]);
                } catch (error) {
                    console.error("Error deleting parcel at index", i, error);
                }
            }
        }
    }

    function renderList(rows) {
        if (!rows || rows.length === 0) {
            return null;
        }

        const dataSorted = [...rows];

        if (["NSW", "QLD", "SA", "VIC", "WA"].includes(normalizedStateCode)) {
            dataSorted.sort((a, b) => {
                const planNumberA = Number(a.plan_number || 0);
                const planNumberB = Number(b.plan_number || 0);
                const lotNumberA = Number(a.lot_number || 0);
                const lotNumberB = Number(b.lot_number || 0);

                return planNumberA - planNumberB || lotNumberA - lotNumberB;
            });
        } else if (normalizedStateCode === "ACT") {
            dataSorted.sort((a, b) => {
                const blockA = Number(a.block || 0);
                const blockB = Number(b.block || 0);
                const sectionA = Number(a.section || 0);
                const sectionB = Number(b.section || 0);

                return blockA - blockB || sectionA - sectionB;
            });
        }

        return [{}].concat(dataSorted.slice(0, 5)).map((parcel, index) =>
            index !== 0 ? (
                <ListGroup.Item key={parcel.parcel_id || parcel._client_id || index}>
                    {parcel.jurisdiction_id || "(No parcel description)"}
                </ListGroup.Item>
            ) : (
                ""
            )
        );
    }

    function renderComponent() {
        return (
            <div className="title-details">
                {showAlert && <Alert variant="danger">{errorMessage}</Alert>}
                {controlType === "table" && (
                    <Row className="show-Container">
                        <Col className="text-right">
                            <Button
                                id="title-details-add-button"
                                variant="secondary"
                                size="sm"
                                onClick={addRecord}
                            >
                                Add
                            </Button>
                            &nbsp;
                            <Button
                                id="title-details-delete-all-button"
                                type="button"
                                size="sm"
                                variant="danger"
                                onClick={deleteAll}
                            >
                                Delete All
                            </Button>
                        </Col>
                    </Row>
                )}

                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {isLoading ? (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color="#e03c31"
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        ) : controlType === "table" ? (
                            <GenericTable
                                columns={columns}
                                data={tableData}
                                section="title-details"
                                pageSize={10}
                                meta={tableMeta}
                            />
                        ) : (
                            renderList(tableData)
                        )}
                    </Col>
                </Row>
            </div>
        );
    }

    return <div className="TitleDetailsSection">{isAuthenticated ? renderComponent() : null}</div>;
}

export default TitleDetailsList;
